import React, { useState, useMemo } from 'react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, X, Filter, Users } from 'lucide-react';

/**
 * Reusable search + squad filter for athlete lists.
 * Returns filtered athletes array via render prop or callback.
 * 
 * searchFirst: When true, shows a clean empty state until user types or filters.
 *              Prevents dumping all athletes by default. Scalable for 100+ athletes.
 */
const AthleteSearchFilter = ({
  athletes, onFiltered, children, compact = false, searchFirst = false,
  showSquadFilter = true, testIdPrefix = 'athlete', label = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [squadFilter, setSquadFilter] = useState('');
  const [browseAll, setBrowseAll] = useState(false);

  const squads = useMemo(() => {
    const s = new Set();
    athletes.forEach(a => { if (a.squad) s.add(a.squad); });
    return [...s].sort();
  }, [athletes]);

  const filtered = useMemo(() => {
    let result = athletes;
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter(a =>
        a.full_name?.toLowerCase().includes(q) ||
        a.position?.toLowerCase().includes(q) ||
        a.nationality?.toLowerCase().includes(q)
      );
    }
    if (squadFilter) {
      result = result.filter(a => a.squad === squadFilter);
    }
    return result;
  }, [athletes, searchTerm, squadFilter]);

  // Notify parent of filtered results
  React.useEffect(() => {
    if (onFiltered) onFiltered(filtered);
  }, [filtered, onFiltered]);

  const hasActiveFilter = searchTerm.trim() || squadFilter;
  const isSearchFirstHidden = searchFirst && !hasActiveFilter && !browseAll;

  return (
    <div data-testid={`${testIdPrefix}-search-filter`}>
      {label && <label className="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-200" htmlFor={`${testIdPrefix}-search-input`}>{label}</label>}
      <div className={`flex gap-2 ${compact ? 'mb-2' : 'mb-3'}`}>
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <Input
            placeholder="Search athletes..."
            id={`${testIdPrefix}-search-input`}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); if (browseAll) setBrowseAll(false); }}
            className={`pl-8 ${compact ? 'h-8 text-xs' : 'h-9 text-sm'}`}
            data-testid={`${testIdPrefix}-search-input`}
            aria-label={label || 'Search athletes'}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Clear search" data-testid={`${testIdPrefix}-clear-search`}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {showSquadFilter && <div className="relative">
          <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <select
            value={squadFilter}
            onChange={(e) => { setSquadFilter(e.target.value); if (browseAll) setBrowseAll(false); }}
            className={`appearance-none pl-8 pr-6 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${compact ? 'h-8 text-xs' : 'h-9 text-sm'}`}
            data-testid={`${testIdPrefix}-squad-filter`}
          >
            <option value="">All Squads</option>
            {squads.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>}
      </div>
      {hasActiveFilter && (
        <div className={`flex items-center gap-2 ${compact ? 'mb-2' : 'mb-3'}`}>
          <span className="text-xs text-gray-500">{filtered.length} of {athletes.length}</span>
          {squadFilter && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setSquadFilter('')} data-testid={`${testIdPrefix}-clear-squad`}>
              {squadFilter} <X className="w-2.5 h-2.5" />
            </Badge>
          )}
          {searchTerm && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setSearchTerm('')} data-testid={`${testIdPrefix}-clear-term`}>
              "{searchTerm}" <X className="w-2.5 h-2.5" />
            </Badge>
          )}
        </div>
      )}
      {isSearchFirstHidden ? (
        <div className="text-center py-6" data-testid="search-first-prompt">
          <Users className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {athletes.length} athletes available
          </p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            Type to search or select a squad
          </p>
          <button
            onClick={() => setBrowseAll(true)}
            className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline"
            data-testid="browse-all-btn"
          >
            Browse all
          </button>
        </div>
      ) : (
        children ? children(filtered) : null
      )}
    </div>
  );
};

export default AthleteSearchFilter;
